
import { auth } from '../firebase';
// lib/sync/api-client.ts — Backend API calls
import type { SyncLog } from '../../types';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001';

async function getAuthHeader(): Promise<{ Authorization: string }> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

// ---------------------------------------------------------------------------
// Sync (legacy)
// ---------------------------------------------------------------------------

export async function uploadSync(encryptedBlob: string, hash: string) {
  const headers = await getAuthHeader();
  const res = await fetch(`${BASE_URL}/api/sync/upload`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({ encryptedBlob, hash }),
  });
  if (!res.ok) throw new Error(`Sync upload failed: ${res.status}`);
  return res.json();
}

export async function getSyncStatus() {
  const headers = await getAuthHeader();
  const res = await fetch(`${BASE_URL}/api/sync/status`, { headers });
  return res.json();
}

// ---------------------------------------------------------------------------
// Drive Backup
// ---------------------------------------------------------------------------

export interface ConnectDriveResult {
  connected: boolean;
  email: string;
  name: string;
}

/** Check Drive connection status with the backend. */
export async function connectDriveWithBackend(): Promise<ConnectDriveResult> {
  const headers = await getAuthHeader();
  const res = await fetch(`${BASE_URL}/api/backup/connect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error ?? `Drive connect failed: ${res.status}`);
  }
  return res.json();
}

/** Disconnect Drive from the backend. */
export async function disconnectDriveWithBackend(): Promise<void> {
  const headers = await getAuthHeader();
  const res = await fetch(`${BASE_URL}/api/backup/disconnect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error ?? `Drive disconnect failed: ${res.status}`);
  }
}

export interface UploadBackupResult {
  success: boolean;
  fileId: string;
  syncedAt: string;
  backupPath: string;
}

/** Upload an encrypted blob to the user's Drive via the backend. */
export async function uploadBackup(
  encryptedBlob: string,
  hash: string,
  syncId?: string,
): Promise<UploadBackupResult> {
  const headers = await getAuthHeader();
  const res = await fetch(`${BASE_URL}/api/backup/upload`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({ encryptedBlob, hash, syncId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error ?? `Backup upload failed: ${res.status}`);
  }
  return res.json();
}

/** Fetch recent sync logs from the backend. */
export async function getRemoteSyncLogs(): Promise<SyncLog[]> {
  const headers = await getAuthHeader();
  const res = await fetch(`${BASE_URL}/api/backup/logs`, {
    headers: { ...headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error ?? `Failed to fetch remote sync logs: ${res.status}`);
  }
  return res.json();
}

export interface BackupStatusResult {
  connected: boolean;
  email?: string;
  name?: string;
  lastBackup?: {
    syncedAt: string;
    backupPath: string;
    fileId: string;
  } | null;
}

/** Get Drive connection status + last backup metadata. */
export async function getBackupStatus(): Promise<BackupStatusResult> {
  const headers = await getAuthHeader();
  const res = await fetch(`${BASE_URL}/api/backup/status`, {
    headers: { ...headers },
  });
  return res.json();
}

export interface DownloadBackupResult {
  noBackup: boolean;
  encryptedBlob?: string;
  fileId?: string;
  backupPath?: string;
}

/** Download the latest Drive backup blob for decryption and restore. */
export async function downloadBackup(): Promise<DownloadBackupResult> {
  const headers = await getAuthHeader();
  const res = await fetch(`${BASE_URL}/api/backup/download?t=${Date.now()}`, {
    headers: { ...headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error ?? `Backup download failed: ${res.status}`);
  }
  return res.json();
}

export interface DownloadLegacyBackupResult {
  noBackup: boolean;
  rawJson?: string;
  fileId?: string;
  fileName?: string;
}

/** Download the latest Legacy Drive backup blob for decryption and restore. */
export async function downloadLegacyBackup(): Promise<DownloadLegacyBackupResult> {
  const headers = await getAuthHeader();
  const res = await fetch(`${BASE_URL}/api/backup/download-legacy?t=${Date.now()}`, {
    headers: { ...headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error ?? `Legacy backup download failed: ${res.status}`);
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Other
// ---------------------------------------------------------------------------

export async function sendOtp(email: string) {
  const headers = await getAuthHeader();
  const res = await fetch(`${BASE_URL}/api/users/pin-reset/otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({ email }),
  });
  return res.json();
}

export async function verifyOtp(otp: string) {
  const headers = await getAuthHeader();
  const res = await fetch(`${BASE_URL}/api/users/pin-reset/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({ otp }),
  });
  return res.json();
}

export async function getFeatureConfig() {
  const res = await fetch(`${BASE_URL}/api/feature-config`);
  return res.json();
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export async function getUserSettings() {
  const headers = await getAuthHeader();
  const res = await fetch(`${BASE_URL}/api/settings`, {
    headers,
  });
  if (!res.ok) throw new Error(`Failed to fetch settings: ${res.status}`);
  return res.json();
}

export async function putUserSettings(settings: unknown) {
  const headers = await getAuthHeader();
  const res = await fetch(`${BASE_URL}/api/settings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify(settings),
  });
  if (!res.ok) throw new Error(`Failed to save settings: ${res.status}`);
  return res.json();
}

// ---------------------------------------------------------------------------
// PIN State
// ---------------------------------------------------------------------------

export async function getUserPinState() {
  const headers = await getAuthHeader();
  const res = await fetch(`${BASE_URL}/api/users/pin-state?t=${Date.now()}`, {
    headers,
  });
  if (!res.ok) throw new Error(`Failed to fetch pin state: ${res.status}`);
  return res.json();
}

export async function putUserPinState(pinState: unknown) {
  const headers = await getAuthHeader();
  const res = await fetch(`${BASE_URL}/api/users/pin-state`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify(pinState),
  });
  if (!res.ok) throw new Error(`Failed to save pin state: ${res.status}`);
  return res.json();
}

// ---------------------------------------------------------------------------
// Feedback
// ---------------------------------------------------------------------------

export interface SubmitFeedbackPayload {
  type?: 'bug' | 'feature' | 'general';
  message: string;
  metadata?: Record<string, unknown>;
}

export async function submitFeedback(payload: SubmitFeedbackPayload) {
  const headers = await getAuthHeader();
  const res = await fetch(`${BASE_URL}/api/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({
      type: payload.type || 'general',
      message: payload.message,
      metadata: payload.metadata || null,
    }),
  });
  if (!res.ok) throw new Error(`Feedback submission failed: ${res.status}`);
  return res.json();
}
